import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { Label } from '../../../components/ui/label'
import { Switch } from '../../../components/ui/switch'
import { Checkbox } from '../../../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'

interface Props {
  system_defaults: {
    worker_model: string
    pm_model: string
    parallel_agents_enabled: boolean
    verification_enabled: boolean
  }
  ai_models: Record<string, string>
  pm_models: Record<string, string>
  worker_usage: {
    org_counts: Record<string, number>
    project_counts: Record<string, number>
  }
  pm_usage: {
    org_counts: Record<string, number>
    project_counts: Record<string, number>
  }
  all_orgs: Array<{
    id: number
    name: string
    worker_model: string | null
    pm_model: string | null
  }>
  projects_with_overrides: Array<{
    id: number
    name: string
    organization_name: string | null
    worker_model: string | null
    pm_model: string | null
  }>
  totals: {
    organizations: number
    projects: number
  }
}

function Index({ system_defaults, ai_models, pm_models, worker_usage, pm_usage, all_orgs, projects_with_overrides, totals }: Props) {
  const { t } = useTranslation('admin')
  const [workerDefault, setWorkerDefault] = useState(system_defaults.worker_model)
  const [pmDefault, setPmDefault] = useState(system_defaults.pm_model)
  const [isSavingDefaults, setIsSavingDefaults] = useState(false)
  const [bulkWorkerModel, setBulkWorkerModel] = useState('')
  const [bulkPmModel, setBulkPmModel] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showOrgOverrides, setShowOrgOverrides] = useState(false)
  const [showProjectOverrides, setShowProjectOverrides] = useState(false)
  const [orgWorkerFilter, setOrgWorkerFilter] = useState('all')
  const [orgPmFilter, setOrgPmFilter] = useState('all')
  const [projectWorkerFilter, setProjectWorkerFilter] = useState('all')
  const [projectPmFilter, setProjectPmFilter] = useState('all')
  const [confirmAction, setConfirmAction] = useState<'worker' | 'pm' | null>(null)
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<number>>(new Set())
  const [orgSearch, setOrgSearch] = useState('')

  const defaultsChanged = workerDefault !== system_defaults.worker_model || pmDefault !== system_defaults.pm_model

  const handleSaveDefaults = () => {
    setIsSavingDefaults(true)
    router.patch('/admin/models/update_system_defaults', {
      worker_model: workerDefault,
      pm_model: pmDefault
    }, {
      preserveScroll: true,
      onFinish: () => setIsSavingDefaults(false)
    })
  }

  const handleToggleFeatureDefault = (key: 'parallel_agents_enabled' | 'verification_enabled', currentValue: boolean) => {
    router.patch('/admin/models/update_system_defaults', {
      [key]: (!currentValue).toString()
    }, { preserveScroll: true })
  }

  const filteredOrgs = orgSearch
    ? all_orgs.filter(o => o.name.toLowerCase().includes(orgSearch.toLowerCase()))
    : all_orgs

  const toggleOrg = (id: number) => {
    setSelectedOrgIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAllFiltered = () => {
    const filteredIds = filteredOrgs.map(o => o.id)
    const allSelected = filteredIds.every(id => selectedOrgIds.has(id))
    setSelectedOrgIds(prev => {
      const next = new Set(prev)
      filteredIds.forEach(id => allSelected ? next.delete(id) : next.add(id))
      return next
    })
  }

  const handleBulkSetWorker = () => {
    setIsSubmitting(true)
    router.post('/admin/models/bulk_set_worker_model', {
      model: bulkWorkerModel === 'default' ? '' : bulkWorkerModel,
      org_ids: Array.from(selectedOrgIds)
    }, {
      preserveScroll: true,
      onFinish: () => { setIsSubmitting(false); setConfirmAction(null); setSelectedOrgIds(new Set()); setOrgSearch('') }
    })
  }

  const handleBulkSetPm = () => {
    setIsSubmitting(true)
    router.post('/admin/models/bulk_set_pm_model', {
      model: bulkPmModel === 'default' ? '' : bulkPmModel,
      org_ids: Array.from(selectedOrgIds)
    }, {
      preserveScroll: true,
      onFinish: () => { setIsSubmitting(false); setConfirmAction(null); setSelectedOrgIds(new Set()); setOrgSearch('') }
    })
  }

  const orgsWithOverrides = all_orgs.filter(o => o.worker_model || o.pm_model)
  const filteredOrgOverrides = orgsWithOverrides.filter(o =>
    (orgWorkerFilter === 'all' || o.worker_model === orgWorkerFilter) &&
    (orgPmFilter === 'all' || o.pm_model === orgPmFilter)
  )
  const filteredProjectOverrides = projects_with_overrides.filter(p =>
    (projectWorkerFilter === 'all' || p.worker_model === projectWorkerFilter) &&
    (projectPmFilter === 'all' || p.pm_model === projectPmFilter)
  )

  const getModelLabel = (modelId: string | null, models: Record<string, string>) => {
    if (!modelId) return t('organizations.show.modelsPage.bulkActions.systemDefault')
    return models[modelId] || modelId
  }

  return (
    <AdminLayout>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('organizations.show.modelsPage.breadcrumb') }]} />

      <div className="flex flex-1 flex-col gap-4 py-4 md:gap-6 md:py-6">
        <div className="px-4 lg:px-6 space-y-6">

          {/* System Defaults */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('organizations.show.modelsPage.systemDefaults.title')}</CardTitle>
              <CardDescription>{t('organizations.show.modelsPage.systemDefaults.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs">{t('organizations.show.modelsPage.systemDefaults.workerModel')}</Label>
                  <Select value={workerDefault} onValueChange={setWorkerDefault}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ai_models).map(([id, name]) => (
                        <SelectItem key={id} value={id}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs">{t('organizations.show.modelsPage.systemDefaults.pmModel')}</Label>
                  <Select value={pmDefault} onValueChange={setPmDefault}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(pm_models).map(([id, name]) => (
                        <SelectItem key={id} value={id}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {defaultsChanged && (
                <div className="mt-4 flex justify-end">
                  <Button onClick={handleSaveDefaults} disabled={isSavingDefaults} size="sm">
                    {isSavingDefaults ? t('organizations.show.modelsPage.systemDefaults.saving') : t('organizations.show.modelsPage.systemDefaults.save')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feature Defaults */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Feature Defaults</CardTitle>
              <CardDescription>Defaults applied when new projects are created. Existing projects keep their configured values.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Parallel Agents</Label>
                    <p className="text-xs text-muted-foreground">Workers build tasks on feature branches in parallel</p>
                  </div>
                  <Switch
                    checked={system_defaults.parallel_agents_enabled}
                    onCheckedChange={() => handleToggleFeatureDefault('parallel_agents_enabled', system_defaults.parallel_agents_enabled)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Verification (Browser Use)</Label>
                    <p className="text-xs text-muted-foreground">Pilot uses browser automation to verify generated code</p>
                  </div>
                  <Switch
                    checked={system_defaults.verification_enabled}
                    onCheckedChange={() => handleToggleFeatureDefault('verification_enabled', system_defaults.verification_enabled)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>{t('organizations.show.modelsPage.bulkActions.title')}</CardTitle>
              <CardDescription>{t('organizations.show.modelsPage.bulkActions.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-2">
                  <Label>{t('organizations.show.modelsPage.bulkActions.workerLabel')}</Label>
                  <Select value={bulkWorkerModel} onValueChange={setBulkWorkerModel}>
                    <SelectTrigger><SelectValue placeholder={t('organizations.show.modelsPage.bulkActions.selectModel')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">{t('organizations.show.modelsPage.bulkActions.systemDefault')}</SelectItem>
                      {Object.entries(ai_models).map(([id, name]) => (
                        <SelectItem key={id} value={id}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => { setSelectedOrgIds(new Set()); setOrgSearch(''); setConfirmAction('worker') }} disabled={!bulkWorkerModel || isSubmitting} variant="outline">
                  {t('organizations.show.modelsPage.bulkActions.selectOrgs')}
                </Button>
              </div>

              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-2">
                  <Label>{t('organizations.show.modelsPage.bulkActions.pmLabel')}</Label>
                  <Select value={bulkPmModel} onValueChange={setBulkPmModel}>
                    <SelectTrigger><SelectValue placeholder={t('organizations.show.modelsPage.bulkActions.selectModel')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">{t('organizations.show.modelsPage.bulkActions.systemDefault')}</SelectItem>
                      {Object.entries(pm_models).map(([id, name]) => (
                        <SelectItem key={id} value={id}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => { setSelectedOrgIds(new Set()); setOrgSearch(''); setConfirmAction('pm') }} disabled={!bulkPmModel || isSubmitting} variant="outline">
                  {t('organizations.show.modelsPage.bulkActions.selectOrgs')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Usage Stats */}
          <div className="grid grid-cols-2 gap-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('organizations.show.modelsPage.workerUsage.title')}</CardTitle>
                <CardDescription>{t('organizations.show.modelsPage.workerUsage.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(ai_models).map(([id, name]) => {
                    const orgCount = worker_usage.org_counts[id] || 0
                    const projCount = worker_usage.project_counts[id] || 0
                    if (orgCount === 0 && projCount === 0) return null
                    return (
                      <div key={id} className="flex items-center justify-between text-sm">
                        <span>{name}</span>
                        <div className="flex gap-2">
                          {orgCount > 0 && <Badge variant="secondary">{orgCount} orgs</Badge>}
                          {projCount > 0 && <Badge variant="outline">{projCount} projects</Badge>}
                        </div>
                      </div>
                    )
                  })}
                  {Object.values(worker_usage.org_counts).every(c => c === 0) &&
                   Object.values(worker_usage.project_counts).every(c => c === 0) && (
                    <p className="text-sm text-muted-foreground">{t('organizations.show.modelsPage.allDefault')}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('organizations.show.modelsPage.pmUsage.title')}</CardTitle>
                <CardDescription>{t('organizations.show.modelsPage.pmUsage.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(pm_models).map(([id, name]) => {
                    const orgCount = pm_usage.org_counts[id] || 0
                    const projCount = pm_usage.project_counts[id] || 0
                    if (orgCount === 0 && projCount === 0) return null
                    return (
                      <div key={id} className="flex items-center justify-between text-sm">
                        <span>{name}</span>
                        <div className="flex gap-2">
                          {orgCount > 0 && <Badge variant="secondary">{orgCount} orgs</Badge>}
                          {projCount > 0 && <Badge variant="outline">{projCount} projects</Badge>}
                        </div>
                      </div>
                    )
                  })}
                  {Object.values(pm_usage.org_counts).every(c => c === 0) &&
                   Object.values(pm_usage.project_counts).every(c => c === 0) && (
                    <p className="text-sm text-muted-foreground">{t('organizations.show.modelsPage.allDefault')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Organization Overrides (collapsible) */}
          {orgsWithOverrides.length > 0 && (
            <Card className="shadow-none">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => setShowOrgOverrides(!showOrgOverrides)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {showOrgOverrides ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {t('organizations.show.modelsPage.orgOverrides.title')}
                    </CardTitle>
                    <CardDescription className="ml-6">
                      {t('organizations.show.modelsPage.orgOverrides.count', { count: orgsWithOverrides.length })}
                    </CardDescription>
                  </div>
                </div>
                {!showOrgOverrides && (
                  <div className="ml-6 mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(
                      orgsWithOverrides.reduce<Record<string, number>>((acc, org) => {
                        if (org.worker_model) acc[`W: ${ai_models[org.worker_model] || org.worker_model}`] = (acc[`W: ${ai_models[org.worker_model] || org.worker_model}`] || 0) + 1
                        if (org.pm_model) acc[`PM: ${pm_models[org.pm_model] || org.pm_model}`] = (acc[`PM: ${pm_models[org.pm_model] || org.pm_model}`] || 0) + 1
                        return acc
                      }, {})
                    ).map(([label, count]) => (
                      <Badge key={label} variant="outline" className="text-xs font-normal">
                        {label} ({count})
                      </Badge>
                    ))}
                  </div>
                )}
              </CardHeader>
              {showOrgOverrides && (
                <CardContent className="space-y-3">
                  <div className="flex gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{t('organizations.show.modelsPage.table.workerModel')}</Label>
                      <Select value={orgWorkerFilter} onValueChange={setOrgWorkerFilter}>
                        <SelectTrigger className="w-52 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('organizations.show.modelsPage.table.all')}</SelectItem>
                          {Object.entries(ai_models).map(([id, name]) => (
                            <SelectItem key={id} value={id}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{t('organizations.show.modelsPage.table.pmModel')}</Label>
                      <Select value={orgPmFilter} onValueChange={setOrgPmFilter}>
                        <SelectTrigger className="w-52 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('organizations.show.modelsPage.table.all')}</SelectItem>
                          {Object.entries(pm_models).map(([id, name]) => (
                            <SelectItem key={id} value={id}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">{t('organizations.show.modelsPage.table.organization')}</th>
                          <th className="text-left py-2 font-medium">{t('organizations.show.modelsPage.table.workerModel')}</th>
                          <th className="text-left py-2 font-medium">{t('organizations.show.modelsPage.table.pmModel')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrgOverrides.map((org) => (
                          <tr key={org.id} className="border-b last:border-0">
                            <td className="py-2">
                              <Link href={`/admin/organizations/${org.id}`} className="text-primary hover:underline">{org.name}</Link>
                            </td>
                            <td className="py-2">{getModelLabel(org.worker_model, ai_models)}</td>
                            <td className="py-2">{getModelLabel(org.pm_model, pm_models)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Project Overrides (collapsible) */}
          {projects_with_overrides.length > 0 && (
            <Card className="shadow-none">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => setShowProjectOverrides(!showProjectOverrides)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {showProjectOverrides ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {t('organizations.show.modelsPage.projectOverrides.title')}
                    </CardTitle>
                    <CardDescription className="ml-6">
                      {t('organizations.show.modelsPage.projectOverrides.count', { count: projects_with_overrides.length })}
                    </CardDescription>
                  </div>
                </div>
                {!showProjectOverrides && (
                  <div className="ml-6 mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(
                      projects_with_overrides.reduce<Record<string, number>>((acc, proj) => {
                        if (proj.worker_model) acc[`W: ${ai_models[proj.worker_model] || proj.worker_model}`] = (acc[`W: ${ai_models[proj.worker_model] || proj.worker_model}`] || 0) + 1
                        if (proj.pm_model) acc[`PM: ${pm_models[proj.pm_model] || proj.pm_model}`] = (acc[`PM: ${pm_models[proj.pm_model] || proj.pm_model}`] || 0) + 1
                        return acc
                      }, {})
                    ).map(([label, count]) => (
                      <Badge key={label} variant="outline" className="text-xs font-normal">
                        {label} ({count})
                      </Badge>
                    ))}
                  </div>
                )}
              </CardHeader>
              {showProjectOverrides && (
                <CardContent className="space-y-3">
                  <div className="flex gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{t('organizations.show.modelsPage.table.workerModel')}</Label>
                      <Select value={projectWorkerFilter} onValueChange={setProjectWorkerFilter}>
                        <SelectTrigger className="w-52 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('organizations.show.modelsPage.table.all')}</SelectItem>
                          {Object.entries(ai_models).map(([id, name]) => (
                            <SelectItem key={id} value={id}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{t('organizations.show.modelsPage.table.pmModel')}</Label>
                      <Select value={projectPmFilter} onValueChange={setProjectPmFilter}>
                        <SelectTrigger className="w-52 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('organizations.show.modelsPage.table.all')}</SelectItem>
                          {Object.entries(pm_models).map(([id, name]) => (
                            <SelectItem key={id} value={id}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">{t('organizations.show.modelsPage.table.project')}</th>
                          <th className="text-left py-2 font-medium">{t('organizations.show.modelsPage.table.organization')}</th>
                          <th className="text-left py-2 font-medium">{t('organizations.show.modelsPage.table.workerModel')}</th>
                          <th className="text-left py-2 font-medium">{t('organizations.show.modelsPage.table.pmModel')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProjectOverrides.map((project) => (
                          <tr key={project.id} className="border-b last:border-0">
                            <td className="py-2">
                              <Link href={`/admin/projects/${project.id}`} className="text-primary hover:underline">{project.name}</Link>
                            </td>
                            <td className="py-2 text-muted-foreground">{project.organization_name}</td>
                            <td className="py-2">{getModelLabel(project.worker_model, ai_models)}</td>
                            <td className="py-2">{getModelLabel(project.pm_model, pm_models)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

        </div>
      </div>

      {/* Organization Selection Modal */}
      <Dialog open={confirmAction !== null} onOpenChange={(open) => { if (!open && !isSubmitting) { setConfirmAction(null); setOrgSearch('') } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {confirmAction === 'worker'
                ? t('organizations.show.modelsPage.dialog.workerTitle', { model: bulkWorkerModel === 'default' ? t('organizations.show.modelsPage.bulkActions.systemDefault') : ai_models[bulkWorkerModel] || bulkWorkerModel })
                : t('organizations.show.modelsPage.dialog.pmTitle', { model: bulkPmModel === 'default' ? t('organizations.show.modelsPage.bulkActions.systemDefault') : pm_models[bulkPmModel] || bulkPmModel })
              }
            </DialogTitle>
            <DialogDescription>{t('organizations.show.modelsPage.dialog.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              placeholder={t('organizations.show.modelsPage.dialog.search')}
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
            />

            <div className="flex items-center gap-2 px-1">
              <Checkbox
                checked={filteredOrgs.length > 0 && filteredOrgs.every(o => selectedOrgIds.has(o.id))}
                onCheckedChange={toggleAllFiltered}
              />
              <span className="text-sm font-medium">
                {orgSearch
                  ? t('organizations.show.modelsPage.dialog.selectAllMatching', { count: filteredOrgs.length })
                  : t('organizations.show.modelsPage.dialog.selectAll')
                }
              </span>
            </div>

            <div className="max-h-64 overflow-y-auto border rounded-md">
              {filteredOrgs.map((org) => (
                <label
                  key={org.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-0"
                >
                  <Checkbox
                    checked={selectedOrgIds.has(org.id)}
                    onCheckedChange={() => toggleOrg(org.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm">{org.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {confirmAction === 'worker'
                      ? (org.worker_model ? ai_models[org.worker_model] || org.worker_model : t('organizations.show.modelsPage.dialog.default'))
                      : (org.pm_model ? pm_models[org.pm_model] || org.pm_model : t('organizations.show.modelsPage.dialog.default'))
                    }
                  </span>
                </label>
              ))}
              {filteredOrgs.length === 0 && (
                <p className="text-sm text-muted-foreground p-3">{t('organizations.show.modelsPage.dialog.noOrgs')}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmAction(null); setOrgSearch('') }} disabled={isSubmitting}>
              {t('organizations.show.modelsPage.dialog.cancel')}
            </Button>
            <Button
              onClick={() => confirmAction === 'worker' ? handleBulkSetWorker() : handleBulkSetPm()}
              disabled={selectedOrgIds.size === 0 || isSubmitting}
            >
              {isSubmitting ? t('organizations.show.modelsPage.dialog.applying') : t('organizations.show.modelsPage.dialog.apply', { count: selectedOrgIds.size })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  )
}

Index.layout = (page: React.ReactNode) => page

export default Index
